import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import Navbar from "@/components/Navbar";
import NewTripModal from "@/components/NewTripModal";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Sparkles, MapPin, Calendar, Compass, Globe } from "lucide-react";
import { toast } from "sonner";

function TripCard({ t }) {
  return (
    <Link to={`/trips/${t.trip_id}`} data-testid={`trip-card-${t.trip_id}`}
      className="group block rounded-2xl overflow-hidden border border-stone-200 bg-white card-hover">
      <div className="relative h-44 bg-stone-200 overflow-hidden">
        <img src={t.cover_image || "https://images.unsplash.com/photo-1534445867742-43195f401b6c?crop=entropy&cs=srgb&fm=jpg&q=85"} alt="" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"/>
        <div className="absolute inset-0 bg-gradient-to-t from-stone-900/60 to-transparent"/>
        <div className="absolute top-3 left-3 flex items-center gap-1.5 px-2 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-mono-code text-stone-700">
          <MapPin size={12}/> {t.destination}
        </div>
        {t._role === "collaborator" && (
          <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-1 rounded-full bg-stone-900/80 text-white text-[10px] font-mono-code uppercase tracking-wider">
            Shared · {t._permission}
          </div>
        )}
      </div>
      <div className="p-5">
        <div className="font-mono-code text-[10px] uppercase tracking-[0.25em] text-amber-700">Trip №{t.trip_id.slice(-4)}</div>
        <h3 className="font-serif-display text-xl font-semibold text-stone-900 mt-1 group-hover:text-[#C85A32] transition-colors">{t.title}</h3>
        <div className="mt-4 flex items-center justify-between text-xs text-stone-500 font-mono-code">
          <span className="flex items-center gap-1"><Calendar size={12}/> {t.days?.length || 0} days</span>
          <span className="flex items-center gap-1"><Compass size={12}/> {t.currency}</span>
        </div>
      </div>
    </Link>
  );
}

export default function Dashboard() {
  const { user, refresh } = useAuth();
  const [trips, setTrips] = useState([]);
  const [modal, setModal] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/trips");
      setTrips(data);
      refresh(); // keep AI quota counter fresh
    }
    catch { toast.error("Couldn't load trips"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const today = new Date().toISOString().slice(0,10);
  const owned = trips.filter(t => t._role !== "collaborator");
  const shared = trips.filter(t => t._role === "collaborator");
  const upcoming = owned.filter(t => !t.end_date || t.end_date >= today);
  const past = owned.filter(t => t.end_date && t.end_date < today);
  const aiUsed = user?.ai_gens_used || 0;

  return (
    <div className="min-h-screen bg-[#FAF7F2]">
      <Navbar/>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        {/* Hero row */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="font-mono-code text-xs uppercase tracking-[0.25em] text-amber-700 mb-2">Welcome back</div>
            <h1 className="font-serif-display text-4xl sm:text-5xl font-bold text-stone-900">Your travel journal, {user?.name?.split(" ")[0]}.</h1>
            <p className="text-stone-600 mt-2 max-w-xl">Every itinerary starts as a whisper of curiosity. Craft your next one below.</p>
          </div>
          <Button onClick={()=>setModal(true)} className="bg-[#C85A32] hover:bg-[#B04B26] text-white h-11 rounded-xl px-5" data-testid="dashboard-new-trip-button">
            <Plus size={16} className="mr-1.5"/> New Trip
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10">
          <div className="p-5 rounded-2xl bg-white border border-stone-200">
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Total Trips</div>
            <div className="font-serif-display text-3xl font-bold mt-1">{trips.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-white border border-stone-200">
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Upcoming</div>
            <div className="font-serif-display text-3xl font-bold mt-1">{upcoming.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-white border border-stone-200">
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-stone-500">Past</div>
            <div className="font-serif-display text-3xl font-bold mt-1">{past.length}</div>
          </div>
          <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
            <div className="font-mono-code text-[10px] uppercase tracking-widest text-amber-800 flex items-center gap-1"><Sparkles size={10}/> {user?.plan === "pro" ? "Plan" : "AI Quota"}</div>
            {user?.plan === "pro" ? (
              <>
                <div className="font-serif-display text-3xl font-bold mt-1 text-stone-900">Pro</div>
                <div className="text-[10px] text-amber-800 font-mono-code mt-0.5">Unlimited trips & AI</div>
              </>
            ) : (
              <>
                <div className="font-serif-display text-3xl font-bold mt-1 text-stone-900" data-testid="ai-quota-count">{aiUsed}/3</div>
                <Link to="/upgrade" className="text-[10px] text-amber-800 font-mono-code mt-0.5 underline hover:text-[#C85A32]" data-testid="dashboard-upgrade-link">
                  This month · Upgrade for unlimited →
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Trips lists */}
        <Tabs defaultValue="upcoming">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="upcoming" data-testid="tab-upcoming">Upcoming</TabsTrigger>
            <TabsTrigger value="past" data-testid="tab-past">Past</TabsTrigger>
            <TabsTrigger value="shared" data-testid="tab-shared">Shared with me {shared.length ? `(${shared.length})` : ""}</TabsTrigger>
            <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
          </TabsList>
          {[
            { key: "upcoming", list: upcoming },
            { key: "past", list: past },
            { key: "shared", list: shared },
            { key: "all", list: trips },
          ].map(({key, list}) => (
            <TabsContent key={key} value={key} className="mt-6">
              {loading ? (
                <div className="text-stone-500 font-mono-code text-sm">Loading trips…</div>
              ) : list.length === 0 ? (
                <div className="text-center py-20 border border-dashed border-stone-300 rounded-2xl bg-white">
                  <Globe size={32} className="mx-auto text-stone-400 mb-3"/>
                  <div className="font-serif-display text-2xl font-semibold text-stone-800">No trips yet</div>
                  <p className="text-stone-500 mt-1 mb-4">Let's plan your first journey.</p>
                  <Button onClick={()=>setModal(true)} className="bg-[#C85A32] hover:bg-[#B04B26] text-white" data-testid="empty-new-trip-button">
                    <Plus size={16} className="mr-1.5"/> New Trip
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {list.map(t => <TripCard key={t.trip_id} t={t}/>)}
                </div>
              )}
            </TabsContent>
          ))}
        </Tabs>
      </main>
      <NewTripModal open={modal} onClose={()=>setModal(false)} onCreated={load}/>
    </div>
  );
}
